import { useState, type ReactNode } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { isToday, isYesterday, differenceInCalendarDays, format } from "date-fns"
import {
  CircleArrowRight, CircleCheck, FileText, Gem, Hourglass, MessageCircle, MessagesSquare, ShoppingBag, type LucideIcon,
} from "lucide-react"
import Loader from "@/components/Loader"
import { cn, stripHtml } from "@/lib/utils"
import { useAuthStore } from "@/store"
import {
  useNotificationAction, useNotificationFeed, usePriority,
  type FeedItem, type FeedType, type PriorityRow,
} from "@/sharedData/Order"
import { kanbanColumns } from "@univdiam/ui/lib/KanbanColumns"
import { Button } from "@univdiam/ui/components/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@univdiam/ui/components/tabs"

// Notifications, in the shape most inboxes use (Linear, GitHub):
//   Priority  what needs the team now, derived from order data (attention rules)
//   Other     everything that happened, one sentence per notification, grouped
//             by order and day
// Clicking a notification opens its order. Only Priority feeds the sidebar badge.

const statusTitle = (id: string | null) => kanbanColumns.find((c) => c.id === id)?.title ?? id ?? ""

// 12m, 5h, 3d, 6w
const compactAge = (iso: string) => {
  const min = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60_000))
  if (min < 60) return `${min}m`
  const h = Math.floor(min / 60)
  if (h < 48) return `${h}h`
  const d = Math.floor(h / 24)
  return d < 14 ? `${d}d` : `${Math.floor(d / 7)}w`
}

const KIND: Record<string, { icon: LucideIcon; tint: string }> = {
  customer_waiting: { icon: MessageCircle, tint: "bg-amber-50 text-amber-700" },
  submitted: { icon: CircleCheck, tint: "bg-[#E8F5EE] text-[#1F7A4D]" },
  new_order: { icon: ShoppingBag, tint: "bg-violet-50 text-violet-700" },
  cold_quote: { icon: Hourglass, tint: "bg-sky-50 text-sky-700" },
  chat_message: { icon: MessageCircle, tint: "bg-muted text-foreground/70" },
  discussion_message: { icon: MessagesSquare, tint: "bg-muted text-foreground/70" },
  status_updated: { icon: CircleArrowRight, tint: "bg-muted text-foreground/70" },
  order_created: { icon: ShoppingBag, tint: "bg-muted text-foreground/70" },
  cad_added: { icon: Gem, tint: "bg-muted text-foreground/70" },
  additional_instruction_added: { icon: FileText, tint: "bg-muted text-foreground/70" },
  additional_instruction_updated: { icon: FileText, tint: "bg-muted text-foreground/70" },
  additional_instruction_deleted: { icon: FileText, tint: "bg-muted text-foreground/70" },
}

// Shared layout for both tabs: icon, sentence, context, optional quote, time.
const Item = ({
  kind, title, context, quote, footer, time, timeTone, unread, actions, onOpen,
}: {
  kind: string
  title: ReactNode
  context: string
  quote?: string | null
  footer?: ReactNode
  time: string | null
  timeTone?: string
  unread?: boolean
  actions?: ReactNode
  onOpen: () => void
}) => {
  const { icon: Icon, tint } = KIND[kind] ?? { icon: MessageCircle, tint: "bg-muted text-foreground/70" }
  return (
    <li className="group flex gap-3 px-5 py-4 hover:bg-[#FAFAFA] focus-within:bg-[#FAFAFA]">
      <span className={cn("mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full", tint)} aria-hidden="true">
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <button type="button" onClick={onOpen} className="block w-full rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <p className={cn("text-sm leading-snug", unread ? "text-foreground" : "text-foreground/80")}>{title}</p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{context}</p>
          {quote && (
            <p className="mt-2 line-clamp-2 border-l-2 border-[#E5E5E5] pl-2.5 text-sm text-foreground/80">{quote}</p>
          )}
        </button>
        {(footer || actions) && (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            {footer}
            {actions && (
              <div className="ms-auto flex gap-1 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                {actions}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2 pt-0.5">
        {time && (
          <time dateTime={time} title={new Date(time).toLocaleString()} className={cn("text-xs tabular-nums", timeTone ?? "text-muted-foreground")}>
            {compactAge(time)}
          </time>
        )}
        {unread && <span className="size-2 rounded-full bg-[#883734]" aria-label="Unread" />}
      </div>
    </li>
  )
}

// ---------------------------------------------------------------- Priority

const RULE_ORDER = ["customer_waiting", "submitted", "new_order", "cold_quote"]
const isCustomer = (role?: string) => role === "CUSTOMER" || role === "STAFF"

const PriorityItem = ({ row, onOpen }: { row: PriorityRow; onOpen: () => void }) => {
  const me = useAuthStore((s) => s.user?.id)
  const { mutate, isPending } = useNotificationAction()
  const primary = [...row.rules].sort((a, b) => RULE_ORDER.indexOf(a.id) - RULE_ORDER.indexOf(b.id))[0]
  if (!primary) return null

  const customer = row.c_business_name || row.c_name || "The customer"
  const msg = row.last_message
  const ref = <strong className="font-semibold">{row.order_id}</strong>
  const title = {
    customer_waiting: <><strong className="font-semibold">{msg && isCustomer(msg.sender_role) ? msg.sender_name ?? customer : customer}</strong> is waiting for a reply on {ref}</>,
    submitted: <><strong className="font-semibold">{customer}</strong> submitted an order request for {ref}</>,
    new_order: <><strong className="font-semibold">{customer}</strong> placed a new order, {ref}</>,
    cold_quote: <><strong className="font-semibold">{customer}</strong> hasn't replied to the quote for {ref}</>,
  }[primary.id] ?? <>{primary.label}: {ref}</>
  const others = row.rules.filter((r) => r.id !== primary.id)

  const urgent = row.rules.some((r) => r.urgent)
  const hours = row.wait_since ? (Date.now() - new Date(row.wait_since).getTime()) / 3_600_000 : 0
  const timeTone = !urgent ? undefined : hours >= 24 ? "font-medium text-red-700" : hours >= 1 ? "font-medium text-amber-700" : "text-foreground"

  const claimed = row.claimed_by != null && row.claimed_at
  const mine = claimed && String(row.claimed_by) === String(me)
  const handledLabel = (row.rules.length === 1 && primary.handledLabel) || "Mark handled"
  const markHandled = () => {
    mutate({ kind: "handled", order_id: row.id, rules: row.rules.map((r) => ({ id: r.id, since: r.since })) })
    toast(`${row.order_id}: ${handledLabel.toLowerCase()}`, {
      action: {
        label: "Undo",
        onClick: () => mutate({ kind: "handled", order_id: row.id, rules: row.rules.map((r) => ({ id: r.id, since: null })) }),
      },
    })
  }

  return (
    <Item
      kind={primary.id}
      title={title}
      context={[row.job_title, statusTitle(row.status)].filter(Boolean).join(", ")}
      quote={primary.id === "customer_waiting" && msg && isCustomer(msg.sender_role) ? stripHtml(msg.text) : null}
      time={row.wait_since}
      timeTone={timeTone}
      onOpen={onOpen}
      footer={
        <>
          {others.map((r) => (
            <span key={r.id} className="text-xs text-muted-foreground">Also {r.label.toLowerCase()}</span>
          ))}
          {claimed && (
            <span className="text-xs font-medium text-amber-800">
              {mine ? "You're on it" : `${row.claimed_by_name ?? "Someone"} is on it`}
            </span>
          )}
        </>
      }
      actions={
        <>
          <Button variant="ghost" size="sm" disabled={isPending} onClick={() => mutate({ kind: "claim", order_id: row.id, claim: !mine })}>
            {mine ? "Release" : claimed ? "Take over" : "I'm on it"}
          </Button>
          <Button variant="ghost" size="sm" disabled={isPending} onClick={markHandled}>{handledLabel}</Button>
        </>
      }
    />
  )
}

const PriorityTab = ({ openOrder }: { openOrder: (o: { id: number; ref: string; customer_id: number }) => void }) => {
  const { data, isPending, isError } = usePriority()
  if (isPending) return <Loader />
  if (isError || !data) return <p className="px-5 py-10 text-center text-sm text-muted-foreground">Couldn't load priority items. Refresh the page to try again.</p>

  const urgent = data.rows.filter((r) => r.rules.some((x) => x.urgent))
  const later = data.rows.filter((r) => !r.rules.some((x) => x.urgent))
  const open = (r: PriorityRow) => () => openOrder({ id: r.id, ref: r.order_id, customer_id: r.customer_id })

  return (
    <>
      {urgent.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-muted-foreground">You're all caught up. Nobody is waiting on the team.</p>
      ) : (
        <ul className="divide-y divide-[#F0F0F0]">{urgent.map((r) => <PriorityItem key={r.id} row={r} onOpen={open(r)} />)}</ul>
      )}
      {later.length > 0 && (
        <>
          <h3 className="border-t border-[#E5E5E5] bg-[#FAFAFA] px-5 py-2 text-xs font-medium text-muted-foreground">Not urgent</h3>
          <ul className="divide-y divide-[#F0F0F0]">{later.map((r) => <PriorityItem key={r.id} row={r} onOpen={open(r)} />)}</ul>
        </>
      )}
    </>
  )
}

// ------------------------------------------------------------------- Other

const FILTERS: { id: FeedType | null; label: string }[] = [
  { id: null, label: "All" },
  { id: "messages", label: "Messages" },
  { id: "status", label: "Status changes" },
  { id: "orders", label: "New orders" },
  { id: "cad", label: "CAD uploads" },
  { id: "instructions", label: "Instructions" },
  { id: "discussion", label: "Team discussion" },
]

// Consecutive notifications about the same order, of the same kind, by the
// same person, on the same day read as one line: "sent 3 messages on UD1054".
type Group = { key: string; latest: FeedItem; ids: number[]; unreadIds: number[] }
const groupFeed = (items: FeedItem[]) => {
  const groups: Group[] = []
  for (const n of items) {
    const prev = groups.at(-1)
    const same = prev &&
      prev.latest.order_id === n.order_id &&
      prev.latest.action_type === n.action_type &&
      prev.latest.actor_name === n.actor_name &&
      prev.latest.created_at.slice(0, 10) === n.created_at.slice(0, 10)
    if (same) {
      prev.ids.push(n.id)
      if (!n.is_seen) prev.unreadIds.push(n.id)
    } else {
      groups.push({ key: String(n.id), latest: n, ids: [n.id], unreadIds: n.is_seen ? [] : [n.id] })
    }
  }
  return groups
}

const dayLabel = (iso: string) => {
  const d = new Date(iso)
  if (isToday(d)) return "Today"
  if (isYesterday(d)) return "Yesterday"
  return differenceInCalendarDays(new Date(), d) < 7 ? format(d, "EEEE") : format(d, "d MMMM yyyy")
}

const sentence = (n: FeedItem, count: number): ReactNode => {
  const who = <strong className="font-semibold">{n.actor_name ?? "Someone"}</strong>
  const ref = <strong className="font-semibold">{n.order_ref}</strong>
  const times = count > 1 ? <span className="text-muted-foreground"> ({count} updates)</span> : null
  switch (n.action_type) {
    case "chat_message":
      return <>{who} sent {count > 1 ? `${count} messages` : "a message"} on {ref}</>
    case "discussion_message":
      return <>{who} posted {count > 1 ? `${count} messages` : "a message"} in the team discussion on {ref}</>
    case "status_updated":
      return <>{who} moved {ref} to {statusTitle(n.status)}{times}</>
    case "order_created":
      return <>{who} placed order {ref}</>
    case "cad_added":
      return <>{who} uploaded {count > 1 ? `${count} CADs` : "a CAD"} for {ref}</>
    case "additional_instruction_added":
    case "additional_instruction_updated":
    case "additional_instruction_deleted":
      return <>{who} updated the instructions on {ref}{times}</>
    default:
      return <>{who} updated {ref}</>
  }
}

const OtherTab = ({ openOrder }: { openOrder: (o: { id: number; ref: string; customer_id: number }) => void }) => {
  const [type, setType] = useState<FeedType | null>(null)
  const [unreadOnly, setUnreadOnly] = useState(false)
  const { mutate } = useNotificationAction()
  const { data, isPending, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useNotificationFeed(type, unreadOnly)

  const groups = groupFeed(data?.pages.flatMap((p) => p.rows) ?? [])
  let lastDay = ""

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5 border-b border-[#E5E5E5] px-5 py-3">
        {FILTERS.map((f) => (
          <button
            key={f.label}
            type="button"
            aria-pressed={type === f.id}
            onClick={() => setType(f.id)}
            className={cn(
              "rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors",
              type === f.id ? "bg-[#883734]/10 text-[#883734]" : "text-muted-foreground hover:bg-muted"
            )}
          >
            {f.label}
          </button>
        ))}
        <label className="ms-auto flex cursor-pointer items-center gap-2 text-xs">
          <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} className="size-3.5 accent-[#883734]" />
          Unread only
        </label>
      </div>

      {isPending ? (
        <Loader />
      ) : isError ? (
        <p className="px-5 py-10 text-center text-sm text-muted-foreground">Couldn't load notifications. Refresh the page to try again.</p>
      ) : groups.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-muted-foreground">
          {unreadOnly ? "No unread notifications." : "No notifications here yet."}
        </p>
      ) : (
        <ul>
          {groups.map((g) => {
            const n = g.latest
            const day = dayLabel(n.created_at)
            const header = day !== lastDay ? day : null
            lastDay = day
            return (
              <li key={g.key}>
                {header && <h3 className="bg-[#FAFAFA] px-5 py-2 text-xs font-medium text-muted-foreground">{header}</h3>}
                <ul>
                  <Item
                    kind={n.action_type}
                    title={sentence(n, g.ids.length)}
                    context={[n.job_title, n.c_business_name || n.c_name].filter(Boolean).join(", ")}
                    quote={n.snippet ? stripHtml(n.snippet) : null}
                    time={n.created_at}
                    unread={g.unreadIds.length > 0}
                    onOpen={() => {
                      if (g.unreadIds.length) mutate({ kind: "read", ids: g.unreadIds })
                      openOrder({ id: n.order_id, ref: n.order_ref, customer_id: n.customer_id })
                    }}
                    actions={
                      g.unreadIds.length > 0 ? (
                        <Button variant="ghost" size="sm" onClick={() => mutate({ kind: "read", ids: g.unreadIds })}>Mark as read</Button>
                      ) : undefined
                    }
                  />
                </ul>
              </li>
            )
          })}
        </ul>
      )}

      {hasNextPage && (
        <div className="border-t border-[#F0F0F0] p-2 text-center">
          <Button variant="ghost" size="sm" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? "Loading…" : "Show older"}
          </Button>
        </div>
      )}
      {!isPending && groups.length > 0 && (
        <div className="border-t border-[#F0F0F0] px-5 py-2 text-right">
          <Button variant="ghost" size="sm" onClick={() => mutate({ kind: "read-all" }, { onSuccess: () => toast("Marked all as read") })}>
            Mark all as read
          </Button>
        </div>
      )}
    </>
  )
}

// -------------------------------------------------------------------- Page

const Notifications = () => {
  const navigate = useNavigate()
  const { data: priority } = usePriority()
  // ?tab=other keeps the tab across reloads and makes it linkable.
  const [params, setParams] = useSearchParams()
  const tab = params.get("tab") === "other" ? "other" : "priority"

  const openOrder = ({ id, ref, customer_id }: { id: number; ref: string; customer_id: number }) =>
    navigate(`/order/${id}/${ref}/${customer_id}`)

  return (
    <div className="min-h-screen w-full p-4 md:p-6">
      <div className="mx-auto max-w-3xl">
        <h1 className="font-display text-2xl font-semibold">Notifications</h1>
        <Tabs value={tab} onValueChange={(v) => setParams(v === "other" ? { tab: "other" } : {}, { replace: true })} className="mt-4">
          <TabsList>
            <TabsTrigger value="priority">
              Priority
              {!!priority?.urgentCount && <span className="ms-1.5 tabular-nums text-muted-foreground">{priority.urgentCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="other">Other</TabsTrigger>
          </TabsList>
          <div className="mt-3 overflow-hidden rounded-lg border border-[#E5E5E5] bg-white">
            <TabsContent value="priority" className="m-0"><PriorityTab openOrder={openOrder} /></TabsContent>
            <TabsContent value="other" className="m-0"><OtherTab openOrder={openOrder} /></TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  )
}

export default Notifications
